import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import DashboardLayout from "./components/Layout";
import useRole from "./components/useRole";
import { getVoucherById, createVoucher, updateVoucher } from "../../api/vouchers";
import { getEventByIdAdmin } from "../../api/plan";

const VoucherFormPage = () => {
  const { eventId, id } = useParams();
  const { isAdmin } = useRole(eventId);
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(false);
  const [eventData, setEventData] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues: {
      discountType: "percentage",
      isActive: true,
      minOrderAmount: 0,
      selectedEvents: [eventId] // Automatically select current event for new vouchers
    }
  });

  const discountType = watch("discountType");

  useEffect(() => {
    // the role on this event, which may differ from the one held on others
    if (!isAdmin) {
      window.location.replace("/");
    }
  }, [isAdmin]);

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        const response = await getEventByIdAdmin(eventId);
        setEventData(response?.data?.data);
      } catch (error) {
        console.error("Error fetching event data:", error);
      }
    };

    fetchEventData();
  }, [eventId]);

  useEffect(() => {
    if (isEdit && id) {
      const fetchVoucherData = async () => {
        try {
          setLoading(true);
          const response = await getVoucherById(id);
          const voucher = response?.data?.data;
          
          if (voucher) {
            setValue("code", voucher.code);
            setValue("name.az", voucher.name?.az || "");
            setValue("name.en", voucher.name?.en || "");
            setValue("name.ru", voucher.name?.ru || "");
            setValue("name.tr", voucher.name?.tr || "");
            setValue("description.az", voucher.description?.az || "");
            setValue("description.en", voucher.description?.en || "");
            setValue("description.ru", voucher.description?.ru || "");
            setValue("description.tr", voucher.description?.tr || "");
            setValue("discountType", voucher.discountType);
            setValue("discountValue", voucher.discountValue);
            setValue("minOrderAmount", voucher.minOrderAmount || 0);
            setValue("maxDiscountAmount", voucher.maxDiscountAmount || "");
            setValue("usageLimit", voucher.usageLimit || "");
            setValue("validFrom", voucher.validFrom ? new Date(voucher.validFrom).toISOString().slice(0, 16) : "");
            setValue("validUntil", voucher.validUntil ? new Date(voucher.validUntil).toISOString().slice(0, 16) : "");
            setValue("isActive", voucher.isActive);
            
            // Set event and ticket type selections
            if (voucher.eventIds && voucher.eventIds.length > 0) {
              setValue("selectedEvents", voucher.eventIds);
            }
            if (voucher.ticketTypeIds && voucher.ticketTypeIds.length > 0) {
              setValue("selectedTicketTypes", voucher.ticketTypeIds);
            }
          }
        } catch (error) {
          console.error("Error fetching voucher data:", error);
          alert("Voucher məlumatları yüklənərkən xəta baş verdi");
        } finally {
          setLoading(false);
        }
      };

      fetchVoucherData();
    }
  }, [isEdit, id, setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      // Prepare voucher data
      const voucherData = {
        code: data.code.toUpperCase().trim(),
        name: {
          az: data.name?.az || "",
          en: data.name?.en || "",
          ru: data.name?.ru || "",
          tr: data.name?.tr || ""
        },
        description: {
          az: data.description?.az || "",
          en: data.description?.en || "",
          ru: data.description?.ru || "",
          tr: data.description?.tr || ""
        },
        discountType: data.discountType,
        discountValue: parseFloat(data.discountValue),
        minOrderAmount: parseFloat(data.minOrderAmount) || 0,
        maxDiscountAmount: data.maxDiscountAmount ? parseFloat(data.maxDiscountAmount) : undefined,
        usageLimit: data.usageLimit ? parseInt(data.usageLimit) : undefined,
        validFrom: data.validFrom ? new Date(data.validFrom).toISOString() : new Date().toISOString(),
        validUntil: new Date(data.validUntil).toISOString(),
        isActive: data.isActive,
        eventIds: data.selectedEvents || [],
        ticketTypeIds: data.selectedTicketTypes || []
      };

      if (isEdit) {
        await updateVoucher(id, voucherData);
      } else {
        await createVoucher(voucherData);
      }

      navigate(`/dashboard/${eventId}/vouchers`);
    } catch (error) {
      console.error("Error saving voucher:", error);
      alert(error.response?.data?.message || "Voucher saxlanılarkən xəta baş verdi");
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
  if (!isAdmin) return <Navigate to={`/dashboard/${eventId}`} replace />;

    return (
      <DashboardLayout id={eventId}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout id={eventId}>
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? "Voucher Düzəlt" : "Yeni Voucher Yarat"}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEdit ? "Voucher məlumatlarını yeniləyin" : "Yeni voucher yaradın və endirim təyin edin"}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Əsas Məlumatlar</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Voucher Kodu *
                </label>
                <input
                  {...register("code", { 
                    required: "Voucher kodu tələb olunur",
                    pattern: {
                      value: /^[A-Z0-9_-]+$/,
                      message: "Yalnız böyük hərflər, rəqəmlər, _ və - istifadə edilə bilər"
                    }
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Məsələn: SUMMER2024"
                />
                {errors.code && (
                  <p className="text-red-600 text-sm mt-1">{errors.code.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endirim Növü *
                </label>
                <select
                  {...register("discountType", { required: true })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="percentage">Faiz (%)</option>
                  <option value="fixed">Sabit məbləğ (₼)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Endirim Dəyəri *
                </label>
                <input
                  {...register("discountValue", { 
                    required: "Endirim dəyəri tələb olunur",
                    min: { value: 0.01, message: "Endirim dəyəri 0-dan böyük olmalıdır" },
                    max: { value: discountType === "percentage" ? 100 : undefined, message: "Faiz 100-dən çox ola bilməz" }
                  })}
                  type="number"
                  step="0.01"
                  min="0"
                  max={discountType === "percentage" ? 100 : undefined}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={discountType === "percentage" ? "10" : "5"}
                />
                {errors.discountValue && (
                  <p className="text-red-600 text-sm mt-1">{errors.discountValue.message}</p>
                )}
              </div>

              {discountType === "percentage" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Maksimum Endirim Məbləği (₼)
                  </label>
                  <input
                    {...register("maxDiscountAmount")}
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Məsələn: 50"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Sifariş Məbləği (₼)
                </label>
                <input
                  {...register("minOrderAmount", { min: 0 })}
                  type="number"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  İstifadə Limiti
                </label>
                <input
                  {...register("usageLimit", { min: 1 })}
                  type="number"
                  min="1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Boş buraxın - limitsiz"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Ad və Təsvir</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ad (Azərbaycan)
                </label>
                <input
                  {...register("name.az")}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Məsələn: Yay Endirimi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Təsvir (Azərbaycan)
                </label>
                <textarea
                  {...register("description.az")}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Voucher haqqında ətraflı məlumat"
                />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Müddət</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Başlanğıc Tarixi *
                </label>
                <input
                  {...register("validFrom", { required: "Başlanğıc tarixi tələb olunur" })}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.validFrom && (
                  <p className="text-red-600 text-sm mt-1">{errors.validFrom.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bitmə Tarixi *
                </label>
                <input
                  {...register("validUntil", { required: "Bitmə tarixi tələb olunur" })}
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {errors.validUntil && (
                  <p className="text-red-600 text-sm mt-1">{errors.validUntil.message}</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-semibold mb-4">Tətbiq Sahəsi</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tədbirlər (Boş buraxın - bütün tədbirlərə tətbiq olunur)
                </label>
                <select
                  {...register("selectedEvents")}
                  multiple
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {eventData && (
                    <option 
                      value={eventData._id}
                      selected={!isEdit} // Auto-select current event for new vouchers
                    >
                      {eventData.title}
                    </option>
                  )}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Ctrl/Cmd basılı saxlayaraq birdən çox seçim edə bilərsiniz
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bilet Növləri (Boş buraxın - bütün bilet növlərinə tətbiq olunur)
                </label>
                <select
                  {...register("selectedTicketTypes")}
                  multiple
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {eventData?.ticketTypes?.map((ticketType) => (
                    <option key={ticketType._id} value={ticketType._id}>
                      {ticketType.name?.az} - {ticketType.price}₼
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">
                  Ctrl/Cmd basılı saxlayaraq birdən çox seçim edə bilərsiniz
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Voucher aktivdir
              </label>
            </div>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(`/dashboard/${eventId}/vouchers`)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Ləğv et
            </button>
            <button
              type="submit"
              disabled={isSubmitting || loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
            >
              {isSubmitting || loading ? "Saxlanılır..." : isEdit ? "Yenilə" : "Yarat"}
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default VoucherFormPage;
